import { formatSigned } from "./edge.js";
import { formatDeadline } from "./marketAge.js";
import { gameRow, isFinal } from "./weekGames.js";
import { coverPath, liveCover, minutesPlayed, pathRange } from "./liveCover.js";

/**
 * The Picks page on game day: each of your picks, and each game row, read as
 * it stands right now -- not started, live, or final -- with the live cover
 * chance from `liveCover.js`. Pure, so the page and its tests read the same
 * thing.
 */

/** "4:25 PM" on the day it kicks off, "Mon 8:15 PM" on any other. */
export function kickoffLabel(kickoffAt, now = Date.now()) {
  if (!kickoffAt) return "";
  const label = formatDeadline(new Date(kickoffAt).getTime());
  const today = formatDeadline(now).split(" ")[0];
  return label.startsWith(`${today} `) ? label.slice(today.length + 1) : label;
}

/** "pre", "live" or "final" -- ESPN's word, so a game it has not started is pre. */
export function gameState(game) {
  if (isFinal(game)) return "final";
  return game.status === "in_progress" ? "live" : "pre";
}

function sideLine(game, side) {
  if (game.league_spread == null) return null;
  const home = Number(game.league_spread);
  return side === "home" ? home : -home;
}

/**
 * The market's expected margin for `side` over a full game, as of the
 * deadline: the negated deadline market line from that side (a -3.2 market is
 * a 3.2-point favourite). Falls back to the frozen line without a market.
 */
function marketMarginFor(row, line) {
  if (row.marketLine != null) return -row.marketLine;
  return line == null ? 0 : -line;
}

/**
 * The live read of one side of one game: null unless it is in progress with a
 * score, a clock and a frozen line.
 */
export function sideLiveCover(game, side, row = gameRow(game, side)) {
  if (gameState(game) !== "live") return null;
  const line = sideLine(game, side);
  return liveCover({
    side,
    line,
    homeScore: game.home_score == null ? null : Number(game.home_score),
    awayScore: game.away_score == null ? null : Number(game.away_score),
    period: game.period ?? null,
    clockSeconds: game.clock_seconds ?? null,
    marketMargin: marketMarginFor(row, line),
  });
}

/**
 * One side's chance to cover through its game so far, for the cover chart:
 * null before kickoff. `plays` are ESPN's scoring plays (the picks payload
 * carries them for your own picks; a games-list row fetches its own on tap).
 */
export function sideCoverPath(game, side, plays, row = gameRow(game, side)) {
  const state = gameState(game);
  if (state === "pre") return null;
  const line = sideLine(game, side);
  const live = state === "live" ? sideLiveCover(game, side, row) : null;
  return coverPath({
    side,
    line,
    marketMargin: marketMarginFor(row, line),
    pregame: row.winProbability == null ? null : row.winProbability / 100,
    plays,
    final: state === "final",
    now: state === "final"
      ? { cushion: row.margin }
      : live && { p: live.p, minute: minutesPlayed(game.period, game.clock_seconds) },
  });
}

/**
 * Your picks, in the order the page lists them, each as the card draws it.
 * `pickedGames` are the stored-shape games from /api/v1/picks (they carry the
 * pick and its scoring plays); `weekGames` are the History rows for the same
 * week (they carry the deadline market, the clock and the edge).
 */
export function gamedayPicks(pickedGames, weekGames, now = Date.now()) {
  const byId = new Map(weekGames.map((game) => [game.id, game]));
  return pickedGames
    .map((stored, index) => {
      const game = byId.get(stored.id);
      const side = stored.pick?.side;
      if (!game || !side) return null;

      const row = gameRow(game, side);
      const state = gameState(game);
      const line = sideLine(game, side);
      const abbr = side === "home" ? game.home_abbr : game.away_abbr;
      const opp = side === "home" ? game.away_abbr : game.home_abbr;
      const home = game.home_score == null ? null : Number(game.home_score);
      const away = game.away_score == null ? null : Number(game.away_score);
      const us = side === "home" ? home : away;
      const them = side === "home" ? away : home;
      const pregame = row.winProbability == null ? null : row.winProbability / 100;
      const live = state === "live" ? sideLiveCover(game, side, row) : null;
      const cushion = state === "final" ? row.margin : state === "live" ? row.liveMargin : null;

      let p = pregame;
      if (state === "final") p = cushion == null ? null : (cushion > 0 ? 1 : 0);
      else if (live) p = live.p;

      const path = sideCoverPath(game, side, stored.scoringPlays, row);

      return {
        id: game.id,
        n: index + 1,
        side,
        abbr,
        team: side === "home" ? game.home_team : game.away_team,
        opp,
        home: side === "home",
        line,
        lineText: line == null ? "" : formatSigned(line),
        state,
        us,
        them,
        score: us == null || them == null ? null : `${us}–${them}`,
        clock: state === "live" ? clockLabel(game) : null,
        kickoff: kickoffLabel(game.kickoff_at, now),
        pregame,
        band: row.band,
        cushion,
        live,
        p,
        path,
        range: state === "final" ? pathRange(path) : null,
      };
    })
    .filter(Boolean);
}

/** "Q3 6:20", "Half", "OT 4:11" -- or "Live" when ESPN gave no clock. */
export function clockLabel(game) {
  const period = game.period;
  const clock = game.display_clock;
  if (period == null || !clock) return "Live";
  if (period === 2 && clock === "0:00") return "Half";
  return `${period > 4 ? "OT" : `Q${period}`} ${clock}`;
}

/**
 * The card's header: record from finished picks, the projected total (each
 * pick's chance summed -- expected pool points), the chance of 5-0 (the
 * product), and how many are live. `expected` is the pre-game sum, for the
 * settled week.
 */
export function gamedaySummary(picks) {
  const finals = picks.filter((pick) => pick.state === "final" && pick.cushion != null);
  const wins = finals.filter((pick) => pick.cushion > 0).length;
  const losses = finals.length - wins;
  const known = picks.filter((pick) => pick.p != null);
  return {
    record: `${wins}–${losses}`,
    live: picks.filter((pick) => pick.state === "live").length,
    settled: picks.length > 0 && picks.every((pick) => pick.state === "final"),
    started: picks.some((pick) => pick.state !== "pre"),
    projected: known.reduce((sum, pick) => sum + pick.p, 0),
    sweep: known.length === picks.length ? known.reduce((product, pick) => product * pick.p, 1) : null,
    expected: picks.reduce((sum, pick) => sum + (pick.pregame ?? 0), 0),
  };
}
